/**
 * @file firmware.ts
 * @description 底座固件更新 feature。统一把 Telink_Base 与 VT_nrf54L_Base 底座接入标准 firmware 契约。
 */

import type { usageType } from '../../../../types/manifest'
import type { BaseDockProfile } from '../../capabilities/base-command/profile'
import type { FileEntry } from '../../firmware/shared/FileUtil'
import { BaseDevice } from '../../../../runtime/BaseDevice'
import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'
import { FirmwareFeature } from '../../../../features/firmware/contract'
import { NordicLFirmwareUpgrade, SlotInfo } from '../../firmware/nordic/NordicUpdater'
import {
  RemoteDeviceVersion,
  VersionInfo,
  VersionType,
  DeviceType,
} from '../../../../types/firmware'
import { WCHUpgrader } from '../../firmware/wch/WCHUpgrader'
import { processZip, processWch } from '../../firmware/shared/FileUtil'
import { ScreenUpgrader } from '../../firmware/screen'
import { sendBaseCommand, sendBaseRaw } from '../../protocols/base-command/command-io'
import { FirmwareEvents } from '../../../../../src/events/FirmwareEvents'
import { formatCompactFirmwareVersion } from '../../../../utils/verify'

const VERSION_FILE_NAME = 'version.json'

/** 判断是否为 WebHID HIDDevice 对象。 */
function isHIDDevice(device: unknown): device is HIDDevice {
  return (
    device !== null &&
    typeof device === 'object' &&
    'addEventListener' in device &&
    'opened' in device &&
    'sendFeatureReport' in device &&
    'receiveFeatureReport' in device
  )
}

export class RapooBaseFirmware extends FirmwareFeature {
  private progressScale?: { offset: number; range: number }

  constructor(
    device: BaseDevice,
    private readonly profile: BaseDockProfile,
  ) {
    super(device)
  }

  async getFirmwareVersion(): Promise<VersionInfo[]> {
    if (this.profile.name === 'Telink_Base') {
      return [
        {
          version: await this.getTelinkFirmwareVersion(),
          versionType: VersionType.Firmware,
          deviceType: DeviceType.Base,
        },
      ]
    }

    const versions: VersionInfo[] = [
      {
        version: await this.getVtFirmwareVersion(0x00),
        versionType: VersionType.USB,
        deviceType: DeviceType.Base,
      },
      {
        version: await this.getVtFirmwareVersion(0x01),
        versionType: VersionType.MasterControl,
        deviceType: DeviceType.Base,
      },
    ]

    if (this.hasScreenFirmwareVersion()) {
      versions.push({
        version: await this.getVtScreenFirmwareVersion(),
        versionType: VersionType.Screen,
        deviceType: DeviceType.Base,
      })
    }

    return versions
  }

  async checkFirmwareUpdate(): Promise<VersionInfo[]> {
    const [versions, onlineVersion] = await Promise.all([
      this.getFirmwareVersion(),
      this.getVersionInfo(),
    ])

    return versions.map((version) => ({
      ...version,
      onlineVersion: this.getOnlineVersion(version.versionType, onlineVersion),
      canUpdate: this.canUpdate(version, onlineVersion),
    }))
  }

  async updateFirmware(): Promise<void> {
    if (this.profile.name === 'Telink_Base') {
      throw new DeviceError(
        DeviceErrorCode.NOT_SUPPORTED_VERSION,
        'Telink base firmware update protocol not implemented',
      )
    }

    // 收集所有升级用到的临时设备，在所有阶段完成后一次性关闭。
    // 避免每升级完一个阶段就 close/reopen HID 设备，因为 close 可能触发
    // USB 设备断开，导致后续升级阶段通信失败。
    const openedDevices: BaseDevice[] = []

    try {
      const versions = (await this.checkFirmwareUpdate()).filter((version) => version.canUpdate)
      const needWch = versions.some((version) => version.versionType === VersionType.USB)
      const needNordic = versions.some(
        (version) => version.versionType === VersionType.MasterControl,
      )
      const needScreen = versions.some((version) => version.versionType === VersionType.Screen)

      const onlineVersion = await this.getVersionInfo()
      const supportedUpdateCount = Number(needWch) + Number(needNordic) + Number(needScreen)
      if (supportedUpdateCount === 0) return

      if (needWch) {
        if (!onlineVersion.FirmwareURL) {
          throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Base WCH firmware URL is empty')
        }
        this.progressScale = this.createProgressScale(0, supportedUpdateCount)
        await this.updateWCH(`${this.modelBaseUrl}/${onlineVersion.FirmwareURL}`, openedDevices)
      }

      // Screen 必须在 Nordic 之前执行：Nordic 升级结束后会调用 start_reset()
      // 复位整个底座硬件，导致 WCH 通信路径中断，Screen 写入失败。
      if (needScreen) {
        if (!onlineVersion.ScreenURL) {
          throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Base Screen firmware URL is empty')
        }
        this.progressScale = this.createProgressScale(needWch ? 1 : 0, supportedUpdateCount)
        await this.updateScreen(`${this.modelBaseUrl}/${onlineVersion.ScreenURL}`, openedDevices)
      }

      if (needNordic) {
        if (!onlineVersion.TransparentURL) {
          throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Base Nordic firmware URL is empty')
        }
        this.progressScale = this.createProgressScale(
          Number(needWch) + Number(needScreen),
          supportedUpdateCount,
        )
        await this.updateNordic(
          `${this.modelBaseUrl}/${onlineVersion.TransparentURL}`,
          openedDevices,
        )
      }
    } finally {
      this.progressScale = undefined
      // 所有阶段结束后，一次性关闭所有临时设备，避免中间关闭触发硬件复位
      for (const dev of openedDevices) {
        await this.device.session.channels.releaseDevice(dev)
      }
    }
  }

  private async getTelinkFirmwareVersion(): Promise<string> {
    const data = await sendBaseCommand(
      this.device,
      this.profile.config,
      this.profile.commands.get_firmware,
    )
    return `${data.getUint8(4)}.${data.getUint8(3)}.${data.getUint8(2)}.${data.getUint8(1)}`
  }

  private async getVtFirmwareVersion(deviceSelect: number): Promise<string> {
    const data = await sendBaseCommand(
      this.device,
      this.profile.config,
      this.profile.commands.get_firmware,
      [deviceSelect],
    )
    return `${this.parseCompactFirmwareVersion(data)}`
  }

  private async getVtScreenFirmwareVersion(): Promise<string> {
    const data = await sendBaseRaw(this.device, this.profile.config, [0xd0, 0x02, 0x14])
    return `${data.getUint8(5)}.${data.getUint8(4)}`
  }

  private parseCompactFirmwareVersion(data: DataView): string {
    const value = data.getUint8(1)
    return formatCompactFirmwareVersion(value)
  }

  private async getVersionInfo(): Promise<RemoteDeviceVersion> {
    // todo 本地/测试环境开发使用
    if (import.meta.env.VITE_BUILD_FLAVOR === 'test') {
      this.device.__setFirmwareBaseUrl('https://soft.rapoo.com/RapooDriver/FirmwareTest')
    }

    const versionRes = await fetch(`${this.modelBaseUrl}/${VERSION_FILE_NAME}?t=${Date.now()}`)
    if (!versionRes.ok) {
      throw new DeviceError(DeviceErrorCode.TIMEOUT, 'checkFirmwareUpdate failed')
    }

    const versionData = await versionRes.json()
    if (!versionData.Device || !versionData.Device.length) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'checkFirmwareUpdate failed')
    }
    return versionData.Device[0]
  }

  private getOnlineVersion(type: VersionType, release: RemoteDeviceVersion): string | undefined {
    switch (type) {
      case VersionType.Firmware:
      case VersionType.USB:
        return release.FirmwareVersion
      case VersionType.MasterControl:
        return release.TransparentVersion
      case VersionType.Screen:
        return release.ScreenVersion
      default:
        return undefined
    }
  }

  private canUpdate(version: VersionInfo, onlineVersion: RemoteDeviceVersion): boolean {
    return this.canUpdateByVersion(
      this.getOnlineVersion(version.versionType, onlineVersion),
      version.version,
    )
  }

  private async updateWCH(url: string, openedDevices: BaseDevice[]): Promise<void> {
    const device = await this.createFirmwareUpdateDevice('updateWCH')
    openedDevices.push(device)

    const fileMap = await processZip(url)
    const firmwareData = Object.values(fileMap)[0]?.data as Uint8Array | undefined
    if (!firmwareData) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Base WCH firmware data is empty')
    }

    const upgrader = new WCHUpgrader(device)
    upgrader.setOnProgressCallback((progress) => this.reportProgress(progress))
    upgrader.setOnProgressFinishCallback(() => this.reportProgress(100))

    await upgrader.startUpdate(firmwareData)
    if (!this.progressScale || this.progressScale.offset + this.progressScale.range >= 100) {
      await upgrader.endDeviceUpdate()
    }
  }

  private async updateNordic(url: string, openedDevices: BaseDevice[]): Promise<void> {
    const fileMap = await processZip(url)
    const device = await this.createFirmwareUpdateDevice('update')
    openedDevices.push(device)

    const nativeDevice = device.channel.nativeDevice

    if (!isHIDDevice(nativeDevice)) {
      throw new DeviceError(
        DeviceErrorCode.NOT_SUPPORTED_VERSION,
        'Base Nordic firmware update only supports WebHID HIDDevice',
      )
    }

    if (!nativeDevice.opened) {
      await nativeDevice.open()
    }

    const upgrader = new NordicLFirmwareUpgrade()

    await new Promise<void>(async (resolve, reject) => {
      upgrader.setSmpErrorCallback((errorMsg: string) => {
        reject(new Error(errorMsg))
      })
      upgrader.setDevice(nativeDevice)
      upgrader.setUpdateProgressCallback((progress) => this.reportProgress(progress))
      upgrader.setSlotInfoCallback((curSlotInfo: SlotInfo) => {
        void this.updateNordicSlot(upgrader, fileMap, curSlotInfo).catch(reject)
      })
      upgrader.setUpdateFinishedCallback(async () => {
        try {
          this.reportProgress(100)
          console.log('updateNordic finished')
          await upgrader.start_reset()
          resolve()
        } catch (error) {
          reject(error)
        }
      })

      this.reportProgress(0)
      void upgrader
        .enterUpdateMode([0xb1, 0x01])
        .then(() => upgrader.start_image_get())
        .catch(reject)
    })
  }

  private async updateNordicSlot(
    upgrader: NordicLFirmwareUpgrade,
    fileMap: Record<string, FileEntry>,
    curSlotInfo: SlotInfo,
  ): Promise<void> {
    const firmwareData = this.getNordicFirmwareBySlot(fileMap, curSlotInfo)
    if (!firmwareData) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Base Nordic firmware slot not found')
    }

    upgrader.setFwUint8Array(firmwareData)
    const errorMessage = await upgrader.updateFwSlot()
    if (errorMessage !== null) {
      throw new DeviceError(DeviceErrorCode.WRITE_FAILED, errorMessage)
    }
  }

  private getNordicFirmwareBySlot(
    fileMap: Record<string, FileEntry>,
    curSlotInfo: SlotInfo,
  ): Uint8Array | null {
    const toUpdateSlot = curSlotInfo.slot === 0 ? 1 : 0
    const fileFeature = `slot${toUpdateSlot}`
    for (const fileName in fileMap) {
      if (fileName.includes(fileFeature)) {
        return fileMap[fileName].data
      }
    }
    return null
  }

  private async updateScreen(url: string, openedDevices: BaseDevice[]): Promise<void> {
    const device = await this.createFirmwareUpdateDevice('updateWCH')
    openedDevices.push(device)

    // 屏幕固件可能以 .bin 或 .zip 格式提供
    const isZip = url.toLowerCase().endsWith('.zip')
    let firmwareData: Uint8Array | undefined

    if (isZip) {
      const fileMap = await processZip(url)
      firmwareData = Object.values(fileMap)[0]?.data as Uint8Array | undefined
    } else {
      firmwareData = await processWch(url)
    }

    if (!firmwareData || firmwareData.length === 0) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Base Screen firmware data is empty')
    }

    const upgrader = new ScreenUpgrader(device)
    upgrader.setOnProgressCallback((progress) => this.reportProgress(progress))
    upgrader.setOnProgressFinishCallback(() => this.reportProgress(100))

    await upgrader.startUpdate(firmwareData)
  }

  private async createFirmwareUpdateDevice(usage: usageType): Promise<BaseDevice> {
    const device = await this.device.session.channels.openDevice({ usageType: usage })
    if (!device) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, `Base update device is empty: ${usage}`)
    }
    return device
  }

  private reportProgress(rawProgress: number): void {
    let progress = Math.min(100, Math.max(0, rawProgress))
    if (this.progressScale) {
      progress = this.progressScale.offset + this.progressScale.range * (progress / 100)
    }
    this.device.emit(FirmwareEvents.WritedData, { progress: Math.floor(progress) })
  }

  private createProgressScale(
    index: number,
    total: number,
  ): { offset: number; range: number } | undefined {
    if (total <= 1) return undefined
    const range = 100 / total
    return {
      offset: index * range,
      range,
    }
  }

  private hasScreenFirmwareVersion(): boolean {
    return this.device.manifest.baseDisplay === 'screen'
  }

  private get modelBaseUrl(): string {
    return `${this.device.getFirmwareBaseUrl()}/${this.device.manifest.mainControl}`
  }
}
